import { useState } from 'react';
import { KeyRound, ShieldCheck, UserCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api, errorMessage } from '@/lib/api';
import { PageHeader, DetailRow } from '@/components/ui/misc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/input';
import { toast } from 'sonner';

export function AccountPage() {
  const { user, terminal } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (newPassword !== repeatPassword) {
      toast.error('La nueva contraseña y su repetición no coinciden');
      return;
    }
    setSaving(true);
    try {
      await api('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } });
      toast.success('Contraseña actualizada');
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Mi cuenta" description="Datos de acceso, terminal y seguridad de la sesión" />
      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        <Card>
          <CardHeader title="Perfil" actions={<UserCircle className="size-4 text-primary" />} />
          <CardContent>
            <DetailRow label="Nombre" value={user?.fullName} />
            <DetailRow label="Usuario" value={<span className="font-mono">{user?.username}</span>} />
            <DetailRow label="Email" value={user?.email} />
            <DetailRow label="Roles" value={<div className="flex flex-wrap justify-end gap-1">{user?.roles.map((role) => <Badge key={role.code} tone="outline">{role.name}</Badge>)}</div>} />
            <DetailRow label="Terminal" value={<span className="font-mono">{terminal?.code ?? '—'}</span>} />
            <DetailRow label="Puesto" value={terminal?.name ?? '—'} />
            <DetailRow label="Almacén" value={terminal?.warehouseName ?? '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Cambiar contraseña" description="Mínimo 10 caracteres, con mayúscula, minúscula y número" actions={<KeyRound className="size-4 text-primary" />} />
          <CardContent>
            <div className="grid max-w-xl gap-4">
              <Field label="Contraseña actual" required>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nueva contraseña" required>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
                </Field>
                <Field label="Repetir nueva contraseña" required>
                  <Input type="password" value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} autoComplete="new-password" />
                </Field>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                <ShieldCheck className="size-4 text-emerald-500" /> Al cambiarla se cerrarán las sesiones abiertas en otros equipos.
              </div>
              <div>
                <Button loading={saving} disabled={!currentPassword || newPassword.length < 10 || newPassword !== repeatPassword} onClick={() => void save()}>
                  Guardar contraseña
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
